import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
// import { useMutation } from 'react-query';

import {ArrowTopRightIcon, IconButton, Link, Spinner, Table, TrashIcon, TagIcon, MenuIcon, StarIcon, RefreshIcon} from 'evergreen-ui';

// MutationResult
import {QueryResult, useMutation, useQuery} from '@apollo/client';

import { DELETE_FOOTPRINT, GET_FOOTPRINTS } from '../../helper/gql';
import { Footprint } from '../../interface';

// Date formatting utility
const formatDate = (dateString: string | Date) => {
  const date = new Date(dateString);

  // Check if date is valid
  if (isNaN(date.getTime())) {
    return 'Invalid date';
  }

  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  // Relative time for recent dates
  if (seconds < 60) {
    return 'Just now';
  } else if (minutes < 60) {
    return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  } else if (hours < 24) {
    return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  } else if (days < 7) {
    return `${days} day${days === 1 ? '' : 's'} ago`;
  }

  // Absolute date for older entries
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// URL parsing utilities
const extractDomain = (urlString: string): string => {
  try {
    const url = new URL(urlString);
    let hostname = url.hostname;
    // Remove "www." subdomain if present
    if (hostname.startsWith('www.')) {
      hostname = hostname.substring(4);
    }
    return hostname;
  } catch (error) {
    return urlString;
  }
};

const extractUrlPath = (urlString: string): string => {
  try {
    const url = new URL(urlString);
    const path = url.pathname + url.search + url.hash;
    return path || '/';
  } catch (error) {
    return '/';
  }
};

// Column width constants
const DATE_COLUMN_WIDTH = 140;
const ACTIONS_COLUMN_WIDTH = 190;

function Footprints() {
  const { loading, error, data, refetch }: QueryResult<{ footprints: Footprint[] }> = useQuery(GET_FOOTPRINTS);
  // MutationResult<{ ... }>
  const [mutateFootprintDelete, mutateResult] = useMutation(DELETE_FOOTPRINT);
  const { token } = useParams<{ token: string }>();
  const [tableWidth, setTableWidth] = useState<number | null>(null);
  const [isMeasuring, setIsMeasuring] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState<string | null>(null);

  console.log(`Footprints() - token:`, token);

  useEffect(() => {
    console.log(`Footprints() - useEffect()`);

    // Get the spacer width
    const spacerElement = document.getElementById('qlip-pane-spacer');
    if (spacerElement) {
      const width = spacerElement.offsetWidth;
      console.log(`Footprints() - spacer width:`, width);
      setTableWidth(width);
    }

    // Add resize listener to update width if window resizes
    const handleResize = () => {
      // Hide table first to get accurate spacer width
      setIsMeasuring(true);

      // Wait a bit for the spacer to resize, then measure
      setTimeout(() => {
        const spacerElement = document.getElementById('qlip-pane-spacer');
        if (spacerElement) {
          const width = spacerElement.offsetWidth;
          console.log(`Footprints() - resize spacer width:`, width);
          setTableWidth(width);

          // Show table again
          setIsMeasuring(false);
        }
      }, 50); // Small delay to allow spacer to resize
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  if (loading) return <Spinner size={24} />;
  if (error || !data) {
    console.log(`Footprints() - error:`, error);
    console.log(`Footprints() - data:`, data);
  }
  function deleteFootprint(footprintId: string) {
    console.log(`deleteFootprint() - footprintId:`, footprintId);
    console.log(`deleteFootprint() - token:`, token);
    console.log(`deleteFootprint() - mutateResult:`, mutateResult);
    mutateFootprintDelete({ variables: { footprintId } })
      .then((data) => {
        console.log(`mutateFootprintDelete() - data:`, data);
        console.log(`mutateFootprintDelete() - mutateResult:`, mutateResult);
        refetch();
      })
      .catch((reason) => {
        console.log(`mutateFootprintDelete() - reason:`, reason);
      });
  }
  if (!data?.footprints || data.footprints.length === 0) {
    return (
      <div style={{ background: 'white', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          padding: '12px 16px',
          borderBottom: '1px solid #e1e5e9',
          background: '#f8f9fa'
        }}>
          <IconButton
            icon={RefreshIcon}
            onClick={() => refetch()}
            title="Refresh footprints"
          />
        </div>
        <div style={{
          textAlign: 'center',
          padding: '3rem',
          color: '#666',
          background: '#f8f9fa'
        }}>
          <h3 style={{ color: '#333', marginBottom: '1rem' }}>No footprints found</h3>
          <p>Start browsing to see your website footprints here.</p>
        </div>
      </div>
    );
  }

  // Calculate domain counts
  const domainCounts = data.footprints.reduce((acc, footprint) => {
    const domain = extractDomain(footprint.url);
    acc[domain] = (acc[domain] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // Sort domains alphabetically
  const sortedDomains = Object.keys(domainCounts).sort();

  // Filter footprints based on selected domain
  const filteredFootprints = selectedDomain
    ? data.footprints.filter(fp => extractDomain(fp.url) === selectedDomain)
    : data.footprints;

  // Calculate column widths
  const detailsWidth = tableWidth ? tableWidth - DATE_COLUMN_WIDTH - ACTIONS_COLUMN_WIDTH : undefined;

  return (
    <div style={{ background: 'white', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 16px',
        borderBottom: '1px solid #e1e5e9',
        background: '#f8f9fa',
        gap: '12px',
        flexWrap: 'wrap'
      }}>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            onClick={() => setSelectedDomain(null)}
            style={{
              padding: '6px 12px',
              borderRadius: '16px',
              border: 'none',
              background: selectedDomain === null ? '#667eea' : '#e8e8e8',
              color: selectedDomain === null ? 'white' : '#333',
              fontSize: '0.85rem',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              whiteSpace: 'nowrap'
            }}
            onMouseEnter={(e) => {
              if (selectedDomain !== null) {
                e.currentTarget.style.background = '#d0d0d0';
              }
            }}
            onMouseLeave={(e) => {
              if (selectedDomain !== null) {
                e.currentTarget.style.background = '#e8e8e8';
              }
            }}
          >
            All ({data.footprints.length})
          </button>
          {sortedDomains.map(domain => (
            <button
              key={domain}
              onClick={() => setSelectedDomain(domain)}
              style={{
                padding: '6px 12px',
                borderRadius: '16px',
                border: 'none',
                background: selectedDomain === domain ? '#667eea' : '#e8e8e8',
                color: selectedDomain === domain ? 'white' : '#333',
                fontSize: '0.85rem',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap'
              }}
              title={`${domain} (${domainCounts[domain]} footprint${domainCounts[domain] === 1 ? '' : 's'})`}
              onMouseEnter={(e) => {
                if (selectedDomain !== domain) {
                  e.currentTarget.style.background = '#d0d0d0';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedDomain !== domain) {
                  e.currentTarget.style.background = '#e8e8e8';
                }
              }}
            >
              {domain} ({domainCounts[domain]})
            </button>
          ))}
        </div>
        <IconButton
          icon={RefreshIcon}
          onClick={() => refetch()}
          title="Refresh footprints"
        />
      </div>
      <Table style={{
        width: tableWidth ? `${tableWidth}px` : '100%',
        display: isMeasuring ? 'none' : 'table'
      }}>
        <Table.Head>
          <Table.TextHeaderCell
            style={{
              fontWeight: 600,
              color: '#333',
              background: '#f8f9fa',
              borderBottom: '2px solid #e1e5e9',
              width: `${DATE_COLUMN_WIDTH}px`,
              minWidth: `${DATE_COLUMN_WIDTH}px`,
              maxWidth: `${DATE_COLUMN_WIDTH}px`
            }}
          >
            Date
          </Table.TextHeaderCell>
          <Table.TextHeaderCell
            style={{
              fontWeight: 600,
              color: '#333',
              background: '#f8f9fa',
              borderBottom: '2px solid #e1e5e9',
              width: detailsWidth ? `${detailsWidth}px` : 'auto',
              minWidth: detailsWidth ? `${detailsWidth}px` : 'auto',
              maxWidth: detailsWidth ? `${detailsWidth}px` : 'auto'
            }}
          >
            Details
          </Table.TextHeaderCell>
          <Table.TextHeaderCell
            style={{
              fontWeight: 600,
              color: '#333',
              background: '#f8f9fa',
              borderBottom: '2px solid #e1e5e9',
              width: `${ACTIONS_COLUMN_WIDTH}px`,
              minWidth: `${ACTIONS_COLUMN_WIDTH}px`,
              maxWidth: `${ACTIONS_COLUMN_WIDTH}px`
            }}
          >
            Actions
          </Table.TextHeaderCell>
        </Table.Head>
        <Table.Body>
          {filteredFootprints.map((footprint) => (
            <Table.Row
              key={footprint.id}
              isSelectable
              onSelect={() => console.log(footprint.url)}
              style={{
                borderBottom: '1px solid #e8e8e8',
                transition: 'background-color 0.2s ease',
                height: 'auto'
              }}
            >
              <Table.TextCell
                style={{
                  color: '#666',
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  verticalAlign: 'top',
                  width: `${DATE_COLUMN_WIDTH}px`,
                  minWidth: `${DATE_COLUMN_WIDTH}px`,
                  maxWidth: `${DATE_COLUMN_WIDTH}px`
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <span style={{ fontWeight: 600, color: '#333' }}>
                    {formatDate(footprint.createdAt)}
                  </span>
                  <span style={{ fontSize: '0.75rem', color: '#999' }}>
                    {new Date(footprint.createdAt).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </span>
                  <span style={{ fontSize: '0.75rem', color: '#999' }}>
                    {new Date(footprint.createdAt).toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
              </Table.TextCell>

              <Table.TextCell
                style={{
                  overflow: 'hidden',
                  height: 'auto',
                  padding: '12px 16px',
                  width: detailsWidth ? `${detailsWidth}px` : 'auto',
                  minWidth: detailsWidth ? `${detailsWidth}px` : 'auto',
                  maxWidth: detailsWidth ? `${detailsWidth}px` : 'auto'
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <div style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    <Link
                      href={footprint.url}
                      target="_blank"
                      style={{
                        color: '#667eea',
                        textDecoration: 'none',
                        fontWeight: 600,
                        fontSize: '0.9rem'
                      }}
                      onMouseEnter={(e: any) => e.target.style.textDecoration = 'underline'}
                      onMouseLeave={(e: any) => e.target.style.textDecoration = 'none'}
                      title={extractDomain(footprint.url)}
                    >
                      {extractDomain(footprint.url)}
                    </Link>
                  </div>
                  <div style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    <Link
                      href={footprint.url}
                      target="_blank"
                      style={{
                        color: '#667eea',
                        textDecoration: 'none',
                        fontWeight: 400,
                        fontSize: '0.85rem'
                      }}
                      onMouseEnter={(e: any) => e.target.style.textDecoration = 'underline'}
                      onMouseLeave={(e: any) => e.target.style.textDecoration = 'none'}
                      title={extractUrlPath(footprint.url)}
                    >
                      {extractUrlPath(footprint.url)}
                    </Link>
                  </div>
                  <div style={{
                    color: footprint.title ? '#666' : '#999',
                    fontStyle: footprint.title ? 'normal' : 'italic',
                    fontWeight: '400',
                    fontSize: '0.85rem',
                    lineHeight: '1.4',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }} title={footprint.title || 'No title available'}>
                    {footprint.title || 'No title available'}
                  </div>
                </div>
              </Table.TextCell>
              
              <Table.TextCell
                style={{
                  verticalAlign: 'top',
                  width: `${ACTIONS_COLUMN_WIDTH}px`,
                  minWidth: `${ACTIONS_COLUMN_WIDTH}px`,
                  maxWidth: `${ACTIONS_COLUMN_WIDTH}px`
                }}
              >
                <div style={{
                  display: 'flex',
                  gap: '2px',
                  alignItems: 'center',
                  flexWrap: 'nowrap',
                  justifyContent: 'flex-start'
                }}>
                  <IconButton 
                    icon={TagIcon} 
                    onClick={(e: any) => {
                      e.stopPropagation();
                      console.log('Add tags:', footprint.id);
                    }}
                    title="Add tags"
                  />
                  <IconButton 
                    icon={MenuIcon} 
                    onClick={(e: any) => {
                      e.stopPropagation();
                      console.log('Assign to list:', footprint.id);
                    }}
                    title="Assign to list"
                  />
                  <IconButton 
                    icon={StarIcon} 
                    onClick={(e: any) => {
                      e.stopPropagation();
                      console.log('Toggle favorite:', footprint.id);
                    }}
                    title="Toggle favorite"
                  />
                  <IconButton 
                    icon={ArrowTopRightIcon} 
                    onClick={(e: any) => {
                      e.stopPropagation();
                      window.open(footprint.url, '_blank');
                    }}
                    title="Open in new tab"
                  />
                  <IconButton 
                    icon={TrashIcon} 
                    intent="danger" 
                    onClick={(e: any) => {
                      e.stopPropagation();
                      if (window.confirm('Are you sure you want to delete this footprint?')) {
                        deleteFootprint(footprint.id);
                      }
                    }}
                    title="Delete footprint"
                  />
                </div>
              </Table.TextCell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </div>
  );
}

export { Footprints };
